import type { PayrollDocSettings } from '@/lib/payroll/payrollDocSettings';
import type { PayrollDraft } from '@/lib/payroll/payrollDraftTypes';

export type PayrollDocumentEntryRow = {
  id: string;
  accountCode: string;
  expenseTypeId: string;
  projectId: string;
  beneficiaryKey: string;
  sourceBankAccountId: string;
  destinationBankAccountId: string;
  paymentMethod: string;
  referenceNo: string;
  description: string;
  debit: string;
  credit: string;
};

function formatAmount(n: number): string {
  if (!n) return '';
  return Math.round(n).toLocaleString('en-US');
}

function emptyRow(seed: string): PayrollDocumentEntryRow {
  return {
    id: seed,
    accountCode: '',
    expenseTypeId: '',
    projectId: '',
    beneficiaryKey: '',
    sourceBankAccountId: '',
    destinationBankAccountId: '',
    paymentMethod: '',
    referenceNo: '',
    description: '',
    debit: '',
    credit: '',
  };
}

/**
 * سند استقرار حقوق: ابتدا بستانکار (بدهی به پرسنل)، سپس بدهکار (هزینه به تفکیک پروژه).
 * پرداخت واقعی بعداً با سند جدا: بدهکار بدهی / بستانکار بانک.
 */
export function buildPayrollAccrualDocumentRows(
  draft: PayrollDraft,
  settings: PayrollDocSettings,
): PayrollDocumentEntryRow[] {
  const seed = Date.now();
  const monthLabel = draft.month_label || draft.month;
  const payableCode = settings.payable_account_code.trim();
  const expenseCode = settings.expense_account_code.trim();
  const out: PayrollDocumentEntryRow[] = [];
  let idx = 0;

  const employees = [...(draft.rows || [])]
    .filter((e) => e.net_salary > 0)
    .sort((a, b) => a.employee_name.localeCompare(b.employee_name, 'fa'));

  for (const e of employees) {
    const projects =
      e.project_breakdown?.map((p) => p.project_name).join('، ') || e.project_name || '';
    out.push({
      ...emptyRow(`pay-c-${seed}-${idx++}`),
      accountCode: payableCode,
      beneficiaryKey: `employee-${e.employee_id}`,
      description: `بدهی حقوق ${monthLabel} — ${e.employee_name}${projects ? ` (${projects})` : ''}`,
      credit: formatAmount(e.net_salary),
      debit: '',
    });
  }

  const byProject = new Map<number, { project_name: string; total: number }>();
  for (const r of draft.project_rows || []) {
    if (r.net_salary <= 0) continue;
    const prev = byProject.get(r.project_id);
    if (!prev) {
      byProject.set(r.project_id, { project_name: r.project_name, total: r.net_salary });
    } else {
      prev.total += r.net_salary;
    }
  }

  const projectLines = [...byProject.entries()].sort((a, b) =>
    a[1].project_name.localeCompare(b[1].project_name, 'fa'),
  );

  for (const [projectId, info] of projectLines) {
    out.push({
      ...emptyRow(`pay-d-${seed}-${idx++}`),
      accountCode: expenseCode,
      projectId: String(projectId),
      description: `هزینه حقوق ${monthLabel} — پروژه ${info.project_name}`,
      debit: formatAmount(info.total),
      credit: '',
    });
  }

  if (projectLines.length === 0 && employees.length > 0 && expenseCode) {
    const total = employees.reduce((s, e) => s + e.net_salary, 0);
    if (total > 0) {
      out.push({
        ...emptyRow(`pay-d-all-${seed}-${idx++}`),
        accountCode: expenseCode,
        description: `هزینه حقوق ${monthLabel} — تجمیع`,
        debit: formatAmount(total),
        credit: '',
      });
    }
  }

  if (out.length === 0) {
    return [emptyRow(`pay-pad-${seed}-1`), emptyRow(`pay-pad-${seed}-2`)];
  }

  return out;
}

export function payrollAccrualDescription(draft: PayrollDraft): string {
  return `استقرار حقوق و دستمزد — ${draft.month_label || draft.month}${draft.date_range_label ? ` (${draft.date_range_label})` : ''}`;
}
